import { useState, type KeyboardEvent, useMemo, SetStateAction, Dispatch } from "react";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DatePicker,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  useDebouncedValue,
} from "@tellescope/ui";
import {
  TellescopeModelFromName,
  useAuth,
  useCachedTags,
  useTellescopeInfiniteQuery,
} from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";

export interface InboxFilterOptions {
  readStatus?: "all" | "read" | "unread";
  inboxStatus?: ("New" | "In Progress" | "Resolved" | string)[];
  assignees?: string[];
  tags?: string[];
  patients?: string[];
  channels?: TellescopeModelFromName<"inbox_threads">["type"][];
  dateRange?: {
    from?: Date;
    to?: Date;
  };
  scheduled?: boolean | null;
}

export interface InboxFilterProps {
  filters: InboxFilterOptions;
  onFiltersChange: Dispatch<SetStateAction<InboxFilterOptions>>;
  availableAssignees?: Array<{ id: string; name: string; avatar?: string }>;
  availableTags?: string[];
  availablePatients?: Array<{ id: string; name: string }>;
  className?: string;
}

export function InboxFilter({ filters = {}, onFiltersChange, className = "" }: InboxFilterProps) {
  const { t } = useTranslation("inbox");
  const { selectedOrganization } = useAuth();

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    status: false,
    inboxStatus: false,
    channels: false,
    assignees: false,
    tags: false,
    patients: false,
    dateRange: false,
    scheduled: false,
  });
  const [assigneesSearch, setAssigneesSearch] = useState("");
  const [debouncedAssigneesSearch] = useDebouncedValue(assigneesSearch.toLowerCase());
  const [tagsSearch, setTagsSearch] = useState("");
  const [patientsSearch, setPatientsSearch] = useState("");
  const [debouncedPatientsSearch] = useDebouncedValue(patientsSearch.toLowerCase());
  const [customTagInput, setCustomTagInput] = useState("");
  const [selectedPersonNames, setSelectedPersonNames] = useState<Record<string, string>>({});
  const { data: availableTags } = useCachedTags({ model: "inbox_threads" });
  const { data: users, isLoading: isLoadingUsers } = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: {
      limit: 1000,
      search: debouncedAssigneesSearch
        ? {
            query: debouncedAssigneesSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat().filter(({ id }) => !filters.assignees?.includes(id)),
  });
  const showUsersLoader = useMemo(
    () => isLoadingUsers || debouncedAssigneesSearch !== assigneesSearch.toLowerCase(),
    [assigneesSearch, debouncedAssigneesSearch, isLoadingUsers]
  );
  const { data: endusers, isLoading: isLoadingEndusers } = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      search: debouncedPatientsSearch ? { query: debouncedPatientsSearch } : undefined,
    },
    select: (data) => data.pages.flat().filter(({ id }) => !filters.patients?.includes(id)),
  });
  const showEndusersLoader = useMemo(
    () => isLoadingEndusers || debouncedPatientsSearch !== patientsSearch.toLowerCase(),
    [debouncedPatientsSearch, isLoadingEndusers, patientsSearch]
  );

  const updateFilter = <Key extends keyof InboxFilterOptions>(key: Key, value: InboxFilterOptions[Key]) => {
    onFiltersChange((prev) => ({ ...prev, [key]: value }));
  };

  const toggleArrayItem = (key: "assignees" | "tags" | "patients" | "channels" | "inboxStatus", value: string) => {
    const currentArray = filters[key] || [];
    const shouldAdd = !currentArray.includes(value);
    if (shouldAdd) {
      switch (key) {
        case "assignees": {
          const user = users?.find(({ id }) => id === value);
          if (user) {
            setSelectedPersonNames((prev) => ({
              ...prev,
              [value]: formatName(user),
            }));
          }
          break;
        }
        case "patients": {
          const enduser = endusers?.find(({ id }) => id === value);
          if (enduser) {
            setSelectedPersonNames((prev) => ({
              ...prev,
              [value]: formatName(enduser),
            }));
          }
          break;
        }
      }
    }
    const newArray = shouldAdd ? [...currentArray, value] : currentArray.filter((item) => item !== value);
    updateFilter(key, newArray);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const addCustomTag = () => {
    const trimmedTag = customTagInput.trim();
    if (trimmedTag && !filters.tags?.includes(trimmedTag)) {
      const newTags = [...(filters.tags || []), trimmedTag];
      updateFilter("tags", newTags);
      setCustomTagInput("");
    }
  };

  const handleCustomTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addCustomTag();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setCustomTagInput("");
    }
  };

  // Get count for specific filter sections
  const getSectionCount = (section: string) => {
    switch (section) {
      case "status":
        return filters.readStatus && filters.readStatus !== "all" ? 1 : 0;
      case "inboxStatus":
        return filters.inboxStatus?.length || 0;
      case "channels":
        return filters.channels?.length || 0;
      case "assignees":
        return filters.assignees?.length || 0;
      case "tags":
        return filters.tags?.length || 0;
      case "patients":
        return filters.patients?.length || 0;
      case "dateRange":
        return filters.dateRange?.from || filters.dateRange?.to ? 1 : 0;
      case "scheduled":
        return filters.scheduled !== null && filters.scheduled !== undefined ? 1 : 0;
      default:
        return 0;
    }
  };

  const availableChannels = useMemo(() => {
    const result = ["SMS", "Chat", "Email"];
    if (selectedOrganization?.hasConnectedZendesk && selectedOrganization?.ticketThreadsEnabled) {
      result.push("Zendesk");
    }
    return result;
  }, [selectedOrganization?.hasConnectedZendesk, selectedOrganization?.ticketThreadsEnabled]);

  return (
    <div className={`space-y-4 pb-4 ${className}`}>
      {/* Read Status */}
      <Collapsible open={expandedSections.status} onOpenChange={() => toggleSection("status")}>
        <CollapsibleTrigger
          className="flex w-full items-center justify-between py-2 hover:bg-muted/50 rounded px-2"
          data-trackingid="inbox-filter-read-status-trigger"
        >
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium cursor-pointer">{t("Read Status")}</Label>
            {getSectionCount("status") > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                {getSectionCount("status")}
              </span>
            )}
          </div>
          {expandedSections.status ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <RadioGroup
            value={filters.readStatus || "all"}
            onValueChange={(value) =>
              updateFilter("readStatus", value === "all" ? undefined : (value as "all" | "read" | "unread"))
            }
            className="pl-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all" id="filter-read-status-all" data-trackingid="inbox-filter-read-status-all" />
              <Label htmlFor="filter-read-status-all" className="font-normal cursor-pointer">
                {t("All Messages")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="unread"
                id="filter-read-status-unread"
                data-trackingid="inbox-filter-read-status-unread"
              />
              <Label htmlFor="filter-read-status-unread" className="font-normal cursor-pointer">
                {t("Unread Only")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="read"
                id="filter-read-status-read"
                data-trackingid="inbox-filter-read-status-read"
              />
              <Label htmlFor="filter-read-status-read" className="font-normal cursor-pointer">
                {t("Read Only")}
              </Label>
            </div>
          </RadioGroup>
        </CollapsibleContent>
      </Collapsible>

      {/* Inbox Status */}
      <Collapsible open={expandedSections.inboxStatus} onOpenChange={() => toggleSection("inboxStatus")}>
        <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium cursor-pointer">{t("Inbox Status")}</Label>
            {getSectionCount("inboxStatus") > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                {getSectionCount("inboxStatus")}
              </span>
            )}
          </div>
          {expandedSections.inboxStatus ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="space-y-2 pl-2">
            {[t("New"), t("In Progress"), t("Resolved")].map((status) => (
              <div key={status} className="flex items-center space-x-2">
                <Checkbox
                  id={`inbox-status-${status}`}
                  checked={filters.inboxStatus?.includes(status) || false}
                  onCheckedChange={() => toggleArrayItem("inboxStatus", status)}
                />
                <Label htmlFor={`inbox-status-${status}`} className="font-normal cursor-pointer">
                  {status}
                </Label>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Channels */}
      <Collapsible open={expandedSections.channels} onOpenChange={() => toggleSection("channels")}>
        <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium cursor-pointer">{t("Channels")}</Label>
            {getSectionCount("channels") > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                {getSectionCount("channels")}
              </span>
            )}
          </div>
          {expandedSections.channels ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="space-y-2 pl-2">
            {availableChannels.map((channel) => (
              <div key={channel} className="flex items-center space-x-2">
                <Checkbox
                  id={`channel-${channel}`}
                  checked={filters.channels?.some((entry) => entry === channel) || false}
                  onCheckedChange={() => toggleArrayItem("channels", channel)}
                />
                <Label htmlFor={`channel-${channel}`} className="font-normal cursor-pointer capitalize">
                  {channel === "GroupMMs" ? t("Group MMS") : channel}
                </Label>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Assignees */}
      <Collapsible open={expandedSections.assignees} onOpenChange={() => toggleSection("assignees")}>
        <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium cursor-pointer">{t("Assignees")}</Label>
            {getSectionCount("assignees") > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                {getSectionCount("assignees")}
              </span>
            )}
          </div>
          {expandedSections.assignees ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="space-y-3 pl-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search assignees..."
                value={assigneesSearch}
                onChange={(e) => setAssigneesSearch(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {showUsersLoader ? (
                <div className="h-48 w-full flex align-center justify-center">
                  <Spinner size="md" color="secondary" />
                </div>
              ) : (
                <>
                  {filters.assignees?.map((assigneeId) => (
                    <div key={assigneeId} className="flex items-center space-x-2">
                      <Checkbox
                        id={`assignee-${assigneeId}`}
                        checked
                        onCheckedChange={() => toggleArrayItem("assignees", assigneeId)}
                      />
                      <Label htmlFor={`assignee-${assigneeId}`} className="font-normal cursor-pointer">
                        {selectedPersonNames[assigneeId]}
                      </Label>
                    </div>
                  ))}
                  {users?.slice(0, 8).map((assignee) =>
                    formatName(assignee) ? (
                      <div key={assignee.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`assignee-${assignee.id}`}
                          checked={filters.assignees?.includes(assignee.id) || false}
                          onCheckedChange={() => toggleArrayItem("assignees", assignee.id)}
                        />
                        <Label htmlFor={`assignee-${assignee.id}`} className="font-normal cursor-pointer">
                          {formatName(assignee)}
                        </Label>
                      </div>
                    ) : null
                  )}
                </>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Tags */}
      <Collapsible open={expandedSections.tags} onOpenChange={() => toggleSection("tags")}>
        <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium cursor-pointer">{t("Tags")}</Label>
            {getSectionCount("tags") > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                {getSectionCount("tags")}
              </span>
            )}
          </div>
          {expandedSections.tags ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="space-y-3 pl-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t("Search tags...")}
                value={tagsSearch}
                onChange={(e) => setTagsSearch(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
            <div className="max-h-48 overflow-y-auto flex flex-col gap-2">
              {availableTags
                .filter((tag) => tag.toLowerCase().includes(tagsSearch.toLowerCase()))
                .map((tag) => (
                  <div key={`tag-${tag}`} className="flex items-center space-x-3">
                    <Checkbox
                      id={`tag-${tag}`}
                      checked={filters.tags?.includes(tag) || false}
                      onCheckedChange={() => toggleArrayItem("tags", tag)}
                    />
                    <Label htmlFor={`tag-${tag}`} className="font-normal cursor-pointer flex items-center gap-2">
                      {tag}
                    </Label>
                  </div>
                ))}
              {filters.tags
                ?.filter((tag) => !availableTags.includes(tag))
                .filter((tag) => tag.toLowerCase().includes(tagsSearch.toLowerCase()))
                .map((tag) => (
                  <div key={`tag-${tag}`} className="flex items-center space-x-3">
                    <Checkbox id={`tag-${tag}`} checked={true} onCheckedChange={() => toggleArrayItem("tags", tag)} />
                    <Label htmlFor={`tag-${tag}`} className="font-normal cursor-pointer flex items-center gap-2">
                      {tag}
                    </Label>
                  </div>
                ))}
              {!tagsSearch && (
                <div className="flex items-center space-x-3 mt-0">
                  <Checkbox id="tag-other" checked={false} />
                  <div className="flex-1 flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder={t("Enter tag name...")}
                      value={customTagInput}
                      onChange={(e) => setCustomTagInput(e.target.value)}
                      onKeyDown={handleCustomTagKeyDown}
                      className="h-4 flex-shrink border-none pl-0 py-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 overflow-visible rounded-none focus-within:bg-100"
                      autoFocus
                    />
                    {customTagInput.trim() && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap flex-grow">
                        {t("Press Enter to confirm")}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Patients */}
      <Collapsible open={expandedSections.patients} onOpenChange={() => toggleSection("patients")}>
        <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium cursor-pointer">{t("Patients")}</Label>
            {getSectionCount("patients") > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                {getSectionCount("patients")}
              </span>
            )}
          </div>
          {expandedSections.patients ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="space-y-3 pl-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search patients..."
                value={patientsSearch}
                onChange={(e) => setPatientsSearch(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {showEndusersLoader ? (
                <div className="h-48 w-full flex align-center justify-center">
                  <Spinner size="md" color="secondary" />
                </div>
              ) : (
                <>
                  {filters.patients?.map((patientId) => (
                    <div key={patientId} className="flex items-center space-x-2">
                      <Checkbox
                        id={`assignee-${patientId}`}
                        checked
                        onCheckedChange={() => toggleArrayItem("patients", patientId)}
                      />
                      <Label htmlFor={`assignee-${patientId}`} className="font-normal cursor-pointer">
                        {selectedPersonNames[patientId]}
                      </Label>
                    </div>
                  ))}
                  {endusers?.slice(0, 8).map((patient) =>
                    formatName(patient) ? (
                      <div key={patient.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`patient-${patient.id}`}
                          checked={false}
                          onCheckedChange={() => toggleArrayItem("patients", patient.id)}
                        />
                        <Label htmlFor={`patient-${patient.id}`} className="font-normal cursor-pointer">
                          {formatName(patient)}
                        </Label>
                      </div>
                    ) : null
                  )}
                </>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Date Range */}
      <Collapsible open={expandedSections.dateRange} onOpenChange={() => toggleSection("dateRange")}>
        <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium cursor-pointer">{t("Received Date Range")}</Label>
            {getSectionCount("dateRange") > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                {getSectionCount("dateRange")}
              </span>
            )}
          </div>
          {expandedSections.dateRange ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="pl-2">
            <div className="flex gap-2">
              <DatePicker
                value={filters.dateRange?.from}
                onChange={(date) =>
                  updateFilter("dateRange", {
                    ...filters.dateRange,
                    from: date,
                  })
                }
                placeholder="From date"
              />
              <DatePicker
                value={filters.dateRange?.to}
                onChange={(date) =>
                  updateFilter("dateRange", {
                    ...filters.dateRange,
                    to: date,
                  })
                }
                placeholder="To date"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Scheduled */}
      <Collapsible open={expandedSections.scheduled} onOpenChange={() => toggleSection("scheduled")}>
        <CollapsibleTrigger className="flex w-full items-center justify-between py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium cursor-pointer">{t("Scheduled Messages")}</Label>
            {getSectionCount("scheduled") > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                {getSectionCount("scheduled")}
              </span>
            )}
          </div>
          {expandedSections.scheduled ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <RadioGroup
            value={filters.scheduled === true ? "yes" : filters.scheduled === false ? "no" : "all"}
            onValueChange={(value) => updateFilter("scheduled", value === "all" ? null : value === "yes")}
            className="pl-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all" id="scheduled-all" />
              <Label htmlFor="scheduled-all" className="font-normal cursor-pointer">
                {t("All")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="scheduled-yes" />
              <Label htmlFor="scheduled-yes" className="font-normal cursor-pointer">
                {t("Scheduled Only")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="scheduled-no" />
              <Label htmlFor="scheduled-no" className="font-normal cursor-pointer">
                {t("Not Scheduled")}
              </Label>
            </div>
          </RadioGroup>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
